import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { useTenant } from '../context/TenantContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { branding } = useBranding();
  const { tenant } = useTenant();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Tenant context is authoritative when arriving via Cloudflare Worker or a
  // subdomain that resolves; branding remains as a legacy fallback.
  const tenantBranding = tenant?.type === 'establishment' ? tenant?.branding : null;
  const branded = !!(tenantBranding || branding?.branding_enabled);
  const logoSrc =
    tenantBranding?.logo_url ||
    (branding?.branding_enabled ? branding.logo_url : null) ||
    '/images/simuclinic-logo.png';
  const displayName =
    tenant?.establishment?.short_name ||
    tenant?.establishment?.name ||
    (branding?.branding_enabled ? (branding.public_name || branding.establishment_nombre) : null);
  const subtitle =
    tenantBranding?.welcome_message ||
    (branding?.branding_enabled ? branding.welcome_message : null) ||
    'Sistema de Simulación Clínica';
  const bgUrl = tenantBranding?.login_background_url || (branding?.branding_enabled ? branding.login_background_url : null);
  const poweredBy = tenant ? tenant.powered_by_simuclinic : (branding?.powered_by_simuclinic ?? true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Inicio de sesión exitoso');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
      toast.error('Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const backgroundStyle = bgUrl
    ? { backgroundImage: `linear-gradient(rgba(248,250,252,0.88), rgba(248,250,252,0.88)), url(${bgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : undefined;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" style={backgroundStyle} data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <Link to="/" className="inline-block">
            <img
              src={logoSrc}
              alt={displayName || 'SimuClinic'}
              className={logoSrc && logoSrc !== '/images/simuclinic-logo.png' ? 'max-h-28 w-auto mx-auto' : 'w-72 h-auto mx-auto'}
              style={{ objectFit: 'contain' }}
              data-testid="login-logo"
            />
          </Link>
          {displayName && (
            <h1 className="text-2xl font-bold text-slate-800 mt-4" data-testid="login-establishment-name">{displayName}</h1>
          )}
          <p className="text-slate-500 mt-2 text-lg" data-testid="login-subtitle">{subtitle}</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Iniciar Sesión</CardTitle>
            <CardDescription>
              Ingresa tus credenciales para acceder al sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-md flex items-center gap-2 text-sm">
                  <WarningCircle size={18} />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Correo electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Ingresando...' : 'Ingresar'}
              </Button>

              <div className="text-center space-y-2 text-sm">
                <Link
                  to="/forgot-password"
                  className="text-blue-600 hover:underline block"
                  data-testid="forgot-password-link"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
                <p className="text-slate-500">
                  ¿No tienes cuenta?{' '}
                  <Link
                    to="/register"
                    className="text-blue-600 hover:underline"
                    data-testid="register-link"
                  >
                    Regístrate
                  </Link>
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        {poweredBy && branded && (
          <p className="text-center text-xs text-slate-400 mt-6" data-testid="powered-by-simuclinic">
            Powered by <span className="font-semibold">SimuClinic</span>
          </p>
        )}
      </div>
    </div>
  );
}
